import { ImportMap } from "@app/shared/resources/class-importer-exporter.service";
import { DateTime } from "luxon";
import { Condition, ConditionData, ConditionHelper, ConditionProviders } from "./condition";

export interface TimerCondition extends Condition {
    // all timers
    startTime: number;
    // daily && astro only
    endTime?: number;
    repeatCount?: number;
    hour?: number; // 0-23
    minute?: number; // 0-59
    seconds?: number; // 0-59
    days?: { [k: number]: boolean };
    // astro only
    sunset?: boolean;
    before?: boolean;
    timezone?: string;
    latitude?: number;
    longitude?: number;
}

export interface TimerConditionData extends ConditionData {
    // all timers
    startTime: number;
    // daily && astro only
    endTime?: number;
    repeatCount?: number;
    hour?: number; // 0-23
    minute?: number; // 0-59
    seconds?: number; // 0-59
    days?: { [k: number]: boolean };
    // astro only
    sunset?: boolean;
    before?: boolean;
    tz?: string;
    latitude?: number;
    longitude?: number;
}

export abstract class TimerConditionHelper extends ConditionHelper {
    static hasActiveDays(condition: TimerCondition): boolean {
        return !!condition.days && Object.values(condition.days).filter(day => day).length > 0;
    }

    static isValidRepeat(condition: TimerCondition): boolean {
        let isAbsolute = condition.days === null && condition.repeatCount === null && condition.endTime === null;

        if (condition.conditionType && condition.conditionType !== ConditionProviders.ABSOLUTE_TIMER) {
            isAbsolute = false;
        }

        return (
            isAbsolute ||
            (!isAbsolute &&
                this.hasActiveDays(condition) &&
                // either a repeatCount or endTime is set
                !!(condition.repeatCount || condition.endTime) &&
                // if the endTime is set, it must be less than the startTime
                (!condition.endTime || condition.startTime < condition.endTime))
        );
    }

    static getConditionType(condition: TimerCondition) {
        let conditionType;
        if (condition.days === null || condition.days === undefined) {
            conditionType = ConditionProviders.ABSOLUTE_TIMER;
        } else if (condition.before === null || condition.before === undefined) {
            conditionType = ConditionProviders.DAILY_TIMER;
        } else {
            conditionType = ConditionProviders.ASTRO_TIMER;
        }

        return conditionType;
    }

    static isValid(condition: TimerCondition): boolean {
        let isValid = true;
        const conditionType = this.getConditionType(condition);
        switch (conditionType) {
            case ConditionProviders.ASTRO_TIMER:
                isValid = condition.before !== null && condition.sunset !== null;

            /* eslint-disable no-fallthrough */
            case ConditionProviders.DAILY_TIMER:
                if (isValid) {
                    isValid =
                        condition.days !== null &&
                        condition.hour !== null &&
                        condition.minute !== null &&
                        condition.seconds !== null &&
                        this.isValidRepeat(condition);
                }
            // fall through
            case ConditionProviders.ABSOLUTE_TIMER:
                if (isValid) {
                    isValid = !!condition.startTime;
                }
                break;
            /* eslint-enable no-fallthrough */

            default:
                isValid = false;
        }

        return isValid;
    }

    static readonly importMap: ImportMap<TimerCondition, TimerConditionData> = [
        { i: "name", o: "name" },
        { i: "condition_id", o: "conditionId" },
        { i: "condition_type", o: "conditionType" },
        { i: "startTime", o: "startTime" },
        { i: "hour", o: "hour" },
        { i: "minute", o: "minute" },
        { i: "seconds", o: "seconds" },
        { i: "days", o: "days" },
        { i: "repeatCount", o: "repeatCount" },
        { i: "endTime", o: "endTime" },
        { i: "before", o: "before" },
        { i: "sunset", o: "sunset" },
        { i: "tz", o: "timezone" },
        { i: "latitude", o: "latitude" },
        { i: "longitude", o: "longitude" },
    ];

    static getNullForUndefined(value) {
        return value === undefined ? null : value;
    }

    static getDefault(timezone: string): TimerCondition {
        const currentTime = DateTime.now().setZone(timezone);
        return {
            name: "Timer",
            conditionId: null,
            conditionType: ConditionProviders.ABSOLUTE_TIMER,
            startTime: +currentTime.toMillis(),
            hour: +currentTime.hour,
            minute: +currentTime.minute,
            seconds: 0,
            days: null,
            endTime: null,
            repeatCount: null,
            before: null,
            sunset: null,
            timezone: null,
            latitude: null,
            longitude: null,
        };
    }

    static fromCHObject(condition): TimerCondition {
        return {
            name: condition[this.importMap[0].i],
            conditionId: this.getNullForUndefined(condition[this.importMap[1].i]),
            conditionType: condition[this.importMap[2].i],
            startTime: condition[this.importMap[3].i],
            hour: this.getNullForUndefined(condition[this.importMap[4].i]),
            minute: this.getNullForUndefined(condition[this.importMap[5].i]),
            seconds: this.getNullForUndefined(condition[this.importMap[6].i]),
            days: this.getNullForUndefined(condition[this.importMap[7].i]),
            repeatCount: this.getNullForUndefined(condition[this.importMap[8].i]),
            endTime: this.getNullForUndefined(condition[this.importMap[9].i]),
            before: this.getNullForUndefined(condition[this.importMap[10].i]),
            sunset: this.getNullForUndefined(condition[this.importMap[11].i]),
            timezone: this.getNullForUndefined(condition[this.importMap[12].i]),
            latitude: this.getNullForUndefined(condition[this.importMap[13].i]),
            longitude: this.getNullForUndefined(condition[this.importMap[14].i]),
        };
    }
}

export class AbsoluteTimerConditionHelper extends TimerConditionHelper {
    static readonly importMap: ImportMap<TimerCondition, TimerConditionData> = [
        { i: "name", o: "name" },
        { i: "condition_id", o: "conditionId" },
        { i: "condition_type", o: "conditionType" },
        { i: "startTime", o: "startTime" },
    ];

    static fromCHObject(condition): TimerCondition {
        return {
            name: condition[this.importMap[0].i],
            conditionId: condition[this.importMap[1].i],
            conditionType: condition[this.importMap[2].i],
            startTime: condition[this.importMap[3].i],
            hour: null,
            minute: null,
            seconds: null,
            days: null,
            repeatCount: null,
            endTime: null,
            before: null,
            sunset: null,
            timezone: null,
            latitude: null,
            longitude: null,
        };
    }
}
